import * as React from 'react';
import ShowSetting from '../questionSettings/showSetting';
import Toggle from '../../../../common/Toggle';
import { Question, QuestionQualityControlSettings } from '@nexxt/common/types';
import Select from 'react-select';
import { MAX_DEMERIT_POINTS_OPTIONS } from '@nexxt/common/constants';

interface QualityControlSettingsProps {
    settings: QuestionQualityControlSettings;
    question: Question;
    disabled: boolean;
    setQuestion: (question: Question) => void;
}

const QualityControlSettings = ({
    question,
    settings,
    disabled,
    setQuestion
}: QualityControlSettingsProps): JSX.Element => {
    const { qualityControlSettings } = question.conversationalSettings;
    return (
        <div className="form-group p-4">
            <div className="font-semibold text-lg mb-4">
                Quality Control Checks
            </div>
            <div className="grid grid-cols-2 gap-8">
                <ShowSetting
                    keyName="showCheckDemeritGibberish"
                    setting={settings}
                >
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Gibberish Detection
                            </label>
                            <p>
                                Flags and probes on gibberish answers such as
                                “lakshdfjashdfk”
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        gibberish: value,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            qualityControlSettings: {
                                                ...qualityControlSettings,
                                                enableCheckDemeritGibberish:
                                                    value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    qualityControlSettings.enableCheckDemeritGibberish ||
                                    question.gibberish
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                <ShowSetting
                    keyName="showCheckDemeritUninformative"
                    setting={settings}
                >
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Uninformative Answer Detection
                            </label>
                            <p>
                                Flags and probes on low information responses
                                such as "i don't know" or "nothing"
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        lazyAnswer: value,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            qualityControlSettings: {
                                                ...qualityControlSettings,
                                                enableCheckDemeritUninformative:
                                                    value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    qualityControlSettings.enableCheckDemeritUninformative ||
                                    question.lazyAnswer
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                <ShowSetting
                    keyName="showCheckDemeritDuplicate"
                    setting={settings}
                >
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Duplicate Answer Detection
                            </label>
                            <p>
                                Flags responses which are similar to other
                                questions where this check is enabled.
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            qualityControlSettings: {
                                                ...qualityControlSettings,
                                                enableCheckDemeritDuplicate:
                                                    value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    qualityControlSettings.enableCheckDemeritDuplicate
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                <ShowSetting keyName="showCheckDemeritAi" setting={settings}>
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Demerit AI
                            </label>
                            <p>
                                Flags responses which appear semantically
                                irrelevant, such as copy-and-pasted spam or
                                incoherent rambling.
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            qualityControlSettings: {
                                                ...qualityControlSettings,
                                                enableCheckDemeritAi: value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    qualityControlSettings.enableCheckDemeritAi
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
            </div>
            {(qualityControlSettings?.enableCheckDemeritGibberish ||
                qualityControlSettings?.enableCheckDemeritUninformative ||
                qualityControlSettings?.enableCheckDemeritAi ||
                qualityControlSettings?.enableCheckDemeritDuplicate) && (
                <div>
                    <label className="font-semibold mb-1">
                        Demerit Influence
                    </label>
                    <p>
                        Participants will accrue demerit points from this
                        question if their response(s) are flagged by any of the
                        Quality Control Checks. Select the value below to
                        determine how many demerit points a participant can
                        accrue from this question.
                    </p>
                    <Select
                        options={MAX_DEMERIT_POINTS_OPTIONS}
                        value={MAX_DEMERIT_POINTS_OPTIONS.find(
                            (item) =>
                                item.value ==
                                qualityControlSettings.maxDemeritPoints
                        )}
                        placeholder={
                            'Select maximum Demerit Points to be accrued'
                        }
                        onChange={(value) =>
                            setQuestion({
                                ...question,
                                conversationalSettings: {
                                    ...question.conversationalSettings,
                                    qualityControlSettings: {
                                        ...qualityControlSettings,
                                        maxDemeritPoints: value
                                    }
                                }
                            })
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default QualityControlSettings;
